import AIChatCitationMarker from "../AIChatCitationMarker/AIChatCitationMarker";
import styles from "./AIChatMessage.module.css";

import { Key } from "react";
import { Box, Tooltip, Anchor, Button, Group } from "@mantine/core";
import ReactMarkdown from "react-markdown";
import rehypeKatex from "rehype-katex";
import remarkGfm from "remark-gfm";
import remarkMath from "remark-math";

interface AIChatMessageProps {
	message: any;
	index: Key | null | undefined;
	streamingMessageRef: any;
	streamingMessageIndex: number;
	resendMessage: (index: any) => void;
	filterMessage: (message: any) => string;
	utmSource: string;
	utmMedium: string;
	utmCampaign: string;
}

export default function AIChatMessage({
	message,
	index,
	streamingMessageRef,
	streamingMessageIndex,
	resendMessage,
	filterMessage,
	utmSource,
	utmMedium,
	utmCampaign,
}: AIChatMessageProps) {
	if (message.role === "assistant") {
		return (
			<Box
				className={`${styles.root} ${styles.role_assistant}`}
				ref={index === streamingMessageIndex ? streamingMessageRef : null}
			>
				<ReactMarkdown
					remarkPlugins={[remarkGfm, [remarkMath, { singleDollarTextMath: false }]]}
					rehypePlugins={[rehypeKatex]}
					components={{
						a: (props) => {
							// Get UTM parameters
							const utmParams = new URLSearchParams({
								utm_source: utmSource,
								utm_medium: utmMedium,
								utm_campaign: utmCampaign,
							});
							const href = `${props.href}?${utmParams.toString()}`;

							// Handle citation link
							let content;
							let isCitationMarker: boolean = false;

							if (typeof props.children === "string") {
								const citationPattern = /【(.*?)】/;
								const match = props.children.match(citationPattern);

								if (match) {
									// Extract the content inside the brackets
									content = match[1];
									isCitationMarker = true;
								} else {
									content = props.children;
								}
							}

							// Return link components
							if (isCitationMarker) {
								// CitationMarker
								return <AIChatCitationMarker href={href!} title={props.title!} label={content!} />;
							} else if ("title" in props) {
								// Standard link with tooltip
								return (
									<Tooltip
										label={
											<Anchor
												href={href}
												target="_blank"
												rel="noopener"
												underline="never"
												style={{ color: "inherit" }}
											>
												{props.title}
											</Anchor>
										}
										style={{ pointerEvents: "auto" }}
									>
										<Anchor href={href} target="_blank" rel="noopener">
											{content}
										</Anchor>
									</Tooltip>
								);
							} else {
								// Standard link, no tooltip
								return (
									<Anchor href={href} target="_blank" rel="noopener">
										{content}
									</Anchor>
								);
							}
						},
					}}
				>
					{filterMessage(message)}
				</ReactMarkdown>
			</Box>
		);
	} else {
		const maybeHasError = message.errorMessage ? styles.hasError : "";
		return (
			<Box className={`${styles.root} ${styles.role_user} ${maybeHasError}`}>
				{filterMessage(message)}
				{message.errorMessage && (
					<Group className={styles.error}>
						<Box>{message.errorMessage}</Box>
						{message.errorAllowRetry && (
							<Button variant="outline" size="compact-xs" onClick={() => resendMessage(index)}>
								Retry
							</Button>
						)}
					</Group>
				)}
			</Box>
		);
	}
}
